/**
 * Partners object page: rehosts reseller and tenant leaves without changing their contracts.
 * TMMPDM-0088: three section tabs (Overview, Reports, Settings). Reports and Settings carry a
 * grouped side menu of their leaves (a labelled nav, `aria-current` on the open leaf); the Settings
 * tab counts its Needs-you items and the Agreement and Rate card leaves carry a dot, each from one
 * server field. `?tab=<leaf>` stays the only view state; a leaf click pushes, normalisation replaces.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Chip, Tab, Tabs, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useReturnTo } from '@/hooks/useReturnTo';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PanelErrorBoundary } from '@/components/PanelErrorBoundary';
import { ErrorState, LocalNav, ObjectHeader, SectionCard, useNotify } from '@/components/page';
import { useRecent } from '@/components/palette/useRecent';
import { tenantStatusLabel } from '@/domain/registry';
import { StatusChip } from '@/status';
import { adminApi } from '@/api/tmm/admin';
import { ApiError } from '@/api/errors';
import type {
  ResellerDto,
  ResellerOutOfBand,
  ResellerStandingState,
  ResellerStandingView,
  FleetDashboard,
} from '@/api/tmm/types';
import { PartnerOverview } from './PartnerOverview';
import { PartnerDetailsPanel } from './PartnerDetailsPanel';
import { PartnerTenantPanel, usePartnerTenant } from './PartnerTenantPanel';
import { CreateTenantDialog } from '../tenants/CreateTenantDialog';
import { PartnerSellable } from './PartnerSellable';
import { ResellerGhlPanel } from '../resellers/ResellerGhlPanel';
import { ResellerStandingPanel } from '../resellers/ResellerStandingPanel';
import { ResellerAgreementPanel } from '../resellers/ResellerAgreementPanel';
import { PartnerCustomerRateCardPanel } from '../customerCards/PartnerCustomerRateCardPanel';
import { ResellerCommissionPlansPanel } from '../resellers/ResellerCommissionPlansPanel';
import { ResellerSettlementsPanel } from '../resellers/ResellerSettlementsPanel';
import { ResellerLedgerLeaf } from '../ledger/PartnerLedgerLeaves';
import { ResellerBillingPanel } from '../resellers/ResellerBillingPanel';
import { ResellerNoticesPanel } from '../resellers/ResellerNoticesPanel';
import { ResellerUsersPanel } from '../resellers/ResellerUsersPanel';
import { ResellerInvitationsPanel } from '../resellers/ResellerInvitationsPanel';
import { PeopleLeafLayout } from './peopleLeafLayout';
import { StandingChip } from '../resellers/StandingChip';
import { useResellerStanding } from '../resellers/useResellerStanding';
import {
  useResellerObjectActions,
  type ResellerObjectActions,
} from '../resellers/useResellerObjectActions';
import {
  SECTION_LABELS,
  CAPTIONS,
  GROUPS,
  GROUP_LABELS,
  TAB_LABELS,
  SECTION_KEYS,
  firstTab,
  hasLocalNav,
  resolveTab,
  sectionOf,
  type SectionKey,
  type TabKey,
} from './partnerSections';
import { settingsNeeds, type SettingsNeeds } from './partnerNeedsYou';
import { formatInstant } from '@/format';

type LoadError = 'not-found' | 'generic' | null;

function creationWarningOf(state: unknown): string | undefined {
  return (state as { creationWarning?: string } | null)?.creationWarning;
}
/** TMMPDM-0084: the warning is the billing one (the tenant exists), so the page offers Setup → Payments. */
function creationWarningPaymentsOf(state: unknown): boolean {
  return (state as { creationWarningPayments?: boolean } | null)?.creationWarningPayments === true;
}
function withoutCreationWarning(state: unknown): unknown {
  if (!creationWarningOf(state)) return state;
  const {
    creationWarning: _shown,
    creationWarningPayments: _link,
    ...rest
  } = state as {
    creationWarning?: string;
    creationWarningPayments?: boolean;
  };
  return Object.keys(rest).length ? rest : null;
}

/** A token no catalog line contains: the slug is swapped in around it, so word order stays the copy's. */
const SLUG_TOKEN = '\u0000';

/** "Tenant: Acme (acme)": the tenant's slug is an identifier, so it takes the mono role (TMM-T1). */
function tenantIdentity(t: ReturnType<typeof useCopy>['t'], name: string, slug: string): ReactNode {
  const [before = '', after = ''] = t('partners.tenantIdentity', 'Tenant: {{name}} ({{slug}})', {
    name,
    slug: SLUG_TOKEN,
  }).split(SLUG_TOKEN);
  return (
    <>
      {before}
      <Box component="span" sx={(theme) => theme.typography.mono}>
        {slug}
      </Box>
      {after}
    </>
  );
}

export function PartnerPage() {
  const { resellerId } = useParams();
  return <PartnerContent key={resellerId} resellerIdParam={resellerId} />;
}

function PartnerContent({ resellerIdParam }: { resellerIdParam: string | undefined }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { backTo } = useReturnTo();
  const resellerId = Number(resellerIdParam);
  const validId = Number.isInteger(resellerId) && resellerId > 0;
  const rawTab = searchParams.get('tab');
  const statement = searchParams.get('statement');
  const focusNotices = searchParams.get('focus') === 'notices';
  // `focus=notices` is the notices leaf. A statement selector on Balance and holds opens Statements.
  let activeTab = focusNotices ? 'notices' : resolveTab(rawTab);
  if (activeTab === 'ledger' && statement) activeTab = 'ledgerStatements';
  const activeSection = sectionOf(activeTab);
  const [reseller, setReseller] = useState<ResellerDto | null>(null);
  const [loading, setLoading] = useState(validId);
  const [loadError, setLoadError] = useState<LoadError>(validId ? null : 'not-found');
  const [loadFailure, setLoadFailure] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // The dashboard cache lasts for one Overview visit: changing tab (or reloading) starts a fresh map.
  // The cleanup runs before the next tab's children start their reads.
  const dashboardReads = useRef(new Map<string, Promise<FleetDashboard>>());
  useEffect(
    () => () => {
      dashboardReads.current = new Map();
    },
    [activeTab],
  );
  const tenantDashboard = useCallback((slug: string, query: { from: string; to: string }) => {
    const key = `${slug}:${query.from}:${query.to}`;
    const reads = dashboardReads.current;
    let read = reads.get(key);
    if (!read) {
      read = adminApi.tenantDashboard(slug, query).catch((error: unknown) => {
        reads.delete(key);
        throw error;
      });
      reads.set(key, read);
    }
    return read;
  }, []);
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);
  const [tenantEditOpen, setTenantEditOpen] = useState(false);
  const standingLoad = useResellerStanding(validId ? resellerId : null, reloadToken);
  const standingState = standingLoad.standing?.state ?? null;
  const outOfBand = standingLoad.standing?.outOfBand ?? null;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusHeading = useCallback(() => headingRef.current?.focus(), []);
  const { record: recordRecent, forget: forgetRecent } = useRecent();
  const notify = useNotify();
  const recentGone = t('palette.recentGone', 'That record no longer exists.');
  const recentGoneRef = useRef(recentGone);
  useEffect(() => {
    recentGoneRef.current = recentGone;
  });

  const urlTab = rawTab === null ? 'overview' : rawTab;
  const normalizing = validId && (urlTab !== activeTab || focusNotices);
  useEffect(() => {
    if (!normalizing) return;
    const params = new URLSearchParams(searchParams);
    if (activeTab === 'overview') params.delete('tab');
    else params.set('tab', activeTab);
    params.delete('focus');
    setSearchParams(params, { replace: true, state: withoutCreationWarning(location.state) });
  }, [normalizing, activeTab, searchParams, setSearchParams, location.state]);

  // The step-2 creation warning is shown on arrival, then dropped from history state (shown once).
  const [creationWarning, setCreationWarning] = useState(() => creationWarningOf(location.state));
  const [creationWarningPayments, setCreationWarningPayments] = useState(() =>
    creationWarningPaymentsOf(location.state),
  );
  useEffect(() => {
    // When the URL is also being normalized, that replace already drops the warning from state; a
    // second replace here would use this render's (stale) search and could undo it.
    if (!creationWarningOf(location.state) || normalizing) return;
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: withoutCreationWarning(location.state) },
    );
    // Runs once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!validId) return;
    let cancelled = false;
    adminApi
      .getReseller(resellerId)
      .then((row) => {
        if (cancelled) return;
        setReseller(row);
        setLoadError(null);
        recordRecent({ type: 'partner', key: String(row.id), label: row.name });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadFailure(error);
        const notFound = error instanceof ApiError && error.status === 404;
        setLoadError(notFound ? 'not-found' : 'generic');
        if (notFound && forgetRecent('partner', String(resellerId)))
          notify.info(recentGoneRef.current);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, validId, reloadToken, recordRecent, forgetRecent, notify]);

  const refreshReseller = useCallback(() => setReloadToken((n) => n + 1), []);
  // The header is this component's, in every state of the page (TMM-B7: one `h1` element from the
  // first render to loaded), so what it shows of the loaded partner is read here — and is inert
  // until there is one.
  const tenantLoad = usePartnerTenant(reseller?.tenant?.slug ?? null, reseller ?? undefined);
  const onUpdated = (updated: ResellerDto) => {
    setReseller(updated);
    standingLoad.reload();
  };
  const headerActions = useResellerObjectActions({
    reseller,
    standingState,
    onUpdated,
    onDeleted: () => navigate('/partners', { replace: true }),
    onReload: refreshReseller,
  });
  const checkedTenantMismatch = useRef<number | null>(null);
  useEffect(() => {
    if (reseller?.tenant !== null || reseller.tenantCreatableReason !== 'HAS_TENANT') return;
    if (checkedTenantMismatch.current === reseller.id) return;
    checkedTenantMismatch.current = reseller.id;
    refreshReseller();
  }, [reseller, refreshReseller]);

  /** What a leaf change resets: the one-shot creation warning. */
  const resetTransient = () => {
    setCreationWarning(undefined);
    setCreationWarningPayments(false);
  };
  const selectTab = (tab: TabKey) => {
    resetTransient();
    const params = new URLSearchParams();
    if (tab !== 'overview') params.set('tab', tab);
    setSearchParams(params, { state: withoutCreationWarning(location.state) });
  };

  const listCrumb = { label: t('page.partners.title', 'Partners'), to: backTo('/partners') };

  const page = (header: ReactNode, body: ReactNode) => (
    <>
      {creationWarning && (creationWarningPayments || reseller?.tenant === null) ? (
        <Alert
          severity="warning"
          role="alert"
          action={
            creationWarningPayments ? (
              <Button color="inherit" size="small" onClick={() => selectTab('payments')}>
                {t('partners.creationWarning.openPayments', 'Open Payments')}
              </Button>
            ) : undefined
          }
        >
          {creationWarning}
        </Alert>
      ) : null}
      <Box>
        <PanelErrorBoundary>{header}</PanelErrorBoundary>
        {body}
      </Box>
    </>
  );

  if (loading) {
    const title = t('partners.loadingTitle', 'Partner');
    return page(
      <ObjectHeader
        breadcrumbs={[listCrumb, { label: title }]}
        name={title}
        headingRef={headingRef}
        meta={
          <Typography variant="body1" color="text.secondary">
            {t('common.loadingPage', 'Loading…')}
          </Typography>
        }
      />,
      <LoadingSpinner />,
    );
  }

  if (loadError === 'not-found') {
    const title = t('partners.notFound', 'Partner not found');
    return page(
      <ObjectHeader
        breadcrumbs={[listCrumb, { label: title }]}
        name={title}
        headingRef={headingRef}
      />,
      null,
    );
  }

  if (loadError === 'generic' || !reseller) {
    const title = resellerIdParam ?? '';
    return page(
      <ObjectHeader
        breadcrumbs={[listCrumb, { label: title }]}
        name={title}
        headingRef={headingRef}
      />,
      <ErrorState
        error={loadFailure}
        message={t('resellers.detail.loadError', 'Could not load reseller.')}
        onRetry={refreshReseller}
      />,
    );
  }

  // The detailed tenant read/save is authoritative for these same published summary fields.
  const currentTenant = tenantLoad.tenant;
  const displayReseller: ResellerDto = currentTenant
    ? {
        ...reseller,
        tenant: {
          slug: currentTenant.slug,
          name: currentTenant.name,
          status: currentTenant.status,
          sellable: currentTenant.sellable,
          sellableReason: currentTenant.sellableReason,
        },
      }
    : reseller;
  const createdAt = reseller.createdAt ? formatInstant(reseller.createdAt, i18n.language) : '—';

  return page(
    <ObjectHeader
      breadcrumbs={[listCrumb, { label: reseller.name }]}
      name={reseller.name}
      identifier={reseller.slug}
      secondaryIdentifier={String(reseller.id)}
      headingRef={headingRef}
      meta={
        <Typography variant="body2" color="text.secondary">
          {displayReseller.tenant
            ? tenantIdentity(t, displayReseller.tenant.name, displayReseller.tenant.slug)
            : t('partners.noTenant', 'No tenant yet')}
          {' · '}
          {t('resellers.col.created', 'Created')} {createdAt}
        </Typography>
      }
      nameAdornment={headerActions.menu}
      actions={headerActions.actions}
      status={
        <>
          <StatusChip status={reseller.status} label={tenantStatusLabel(reseller.status, t)} />
          {standingState ? <StandingChip state={standingState} /> : null}
          <PartnerSellable tenant={displayReseller.tenant} />
          {reseller.platformOwned ? (
            <Chip size="small" label={t('partners.house.chip', 'House')} />
          ) : null}
          {standingLoad.standing?.platformManagedSince ? (
            <StatusChip
              tone="info"
              label={t('partners.platformManaged.chip', 'Platform-managed')}
            />
          ) : null}
        </>
      }
    />,
    <LoadedPartner
      reseller={reseller}
      activeTab={activeTab}
      activeSection={activeSection}
      displayReseller={displayReseller}
      focusHeading={focusHeading}
      standing={standingLoad.standing}
      standingState={standingState}
      standingLoading={standingLoad.loading}
      standingFailure={standingLoad.failure}
      onStandingRetry={standingLoad.reload}
      outOfBand={outOfBand}
      invitationsRefreshKey={invitationsRefreshKey}
      onInvited={() => setInvitationsRefreshKey((key) => key + 1)}
      onUpdated={onUpdated}
      onReload={refreshReseller}
      tenantDashboard={tenantDashboard}
      tenantLoad={tenantLoad}
      tenantEditOpen={tenantEditOpen}
      onTenantEditOpenChange={setTenantEditOpen}
      headerActions={headerActions}
      selectTab={selectTab}
      resetTransient={resetTransient}
      leafState={withoutCreationWarning(location.state)}
      settings={settingsNeeds({
        sellableReason: displayReseller.tenant?.sellableReason,
        launchUnset:
          standingLoad.standing !== null && standingLoad.standing.officialLaunchOn === null,
      })}
      onBillingWarning={(warning) => {
        setCreationWarning(warning);
        setCreationWarningPayments(true);
      }}
    />,
  );
}

/** The sections of the loaded partner, under the header `PartnerContent` draws in every state. */
function LoadedPartner({
  reseller,
  displayReseller,
  activeTab,
  activeSection,
  focusHeading,
  standing,
  standingState,
  standingLoading,
  standingFailure,
  onStandingRetry,
  outOfBand,
  invitationsRefreshKey,
  onInvited,
  onUpdated,
  onReload,
  tenantDashboard,
  tenantLoad,
  tenantEditOpen,
  onTenantEditOpenChange,
  headerActions,
  selectTab,
  resetTransient,
  leafState,
  settings,
  onBillingWarning,
}: {
  reseller: ResellerDto;
  displayReseller: ResellerDto;
  activeTab: TabKey;
  activeSection: SectionKey;
  focusHeading: () => void;
  standing: ResellerStandingView | null;
  standingState: ResellerStandingState | null;
  standingLoading: boolean;
  standingFailure: unknown;
  onStandingRetry: () => void;
  outOfBand: ResellerOutOfBand | null;
  invitationsRefreshKey: number;
  onInvited: () => void;
  onUpdated: (reseller: ResellerDto) => void;
  onReload: () => void;
  tenantDashboard: (slug: string, query: { from: string; to: string }) => Promise<FleetDashboard>;
  tenantLoad: ReturnType<typeof usePartnerTenant>;
  tenantEditOpen: boolean;
  onTenantEditOpenChange: (open: boolean) => void;
  headerActions: ResellerObjectActions;
  selectTab: (tab: TabKey) => void;
  /** A leaf link's resets (the same ones `selectTab` makes). */
  resetTransient: () => void;
  /** The history state a leaf link carries (the creation warning dropped). */
  leafState: unknown;
  /** The Settings-targeted Needs-you items: the tab's count and the leaves' dots. */
  settings: SettingsNeeds;
  /** A tenant created on this page whose billing provisioning failed (TMMPDM-0084): the page warns once. */
  onBillingWarning: (warning: string) => void;
}) {
  const { t } = useCopy();
  const [createOpen, setCreateOpen] = useState(false);

  const sectionLabel = (section: SectionKey) =>
    t(`partners.section.${section}`, SECTION_LABELS[section]);

  const renderPanel = (tab: TabKey): ReactNode => {
    switch (tab) {
      case 'overview':
        return (
          <PartnerOverview
            reseller={reseller}
            displayReseller={displayReseller}
            standing={standing}
            standingState={standingState}
            standingLoading={standingLoading}
            standingFailure={standingFailure}
            onStandingRetry={onStandingRetry}
            outOfBand={outOfBand}
            onReload={onReload}
            focusHeading={focusHeading}
            onOpenDeactivate={headerActions.openDeactivate}
            actionsBusy={headerActions.busy}
            tenantDashboard={tenantDashboard}
            tenantLoad={tenantLoad}
            tenantEditOpen={tenantEditOpen}
            onTenantEditOpenChange={onTenantEditOpenChange}
            onCreateTenant={() => setCreateOpen(true)}
            openTab={selectTab}
          />
        );
      case 'standing':
        return (
          <ResellerStandingPanel
            standing={standing}
            failure={standingFailure}
            onRetry={onStandingRetry}
            reseller={reseller}
            onChanged={onReload}
          />
        );
      case 'notices':
        return <NoticesRegion resellerId={reseller.id} />;
      case 'billing':
        return <ResellerBillingPanel resellerId={reseller.id} view="charges" />;
      case 'cardOnFile':
        return <ResellerBillingPanel resellerId={reseller.id} view="card" />;
      case 'details':
        return (
          <PartnerDetailsPanel
            reseller={reseller}
            tenantLoad={tenantLoad}
            tenantEditOpen={tenantEditOpen}
            onTenantEditOpenChange={onTenantEditOpenChange}
            onUpdated={onUpdated}
            onReload={onReload}
            actionsBusy={headerActions.busy}
          />
        );
      case 'agreement':
        return <ResellerAgreementPanel resellerId={reseller.id} />;
      case 'priceCard':
        return <PartnerCustomerRateCardPanel resellerId={reseller.id} />;
      case 'commissionPlans':
        return <ResellerCommissionPlansPanel resellerId={reseller.id} />;
      case 'ledger':
        return <ResellerLedgerLeaf resellerId={reseller.id} name={reseller.name} part="balance" />;
      case 'ledgerStatements':
        return (
          <ResellerLedgerLeaf resellerId={reseller.id} name={reseller.name} part="statements" />
        );
      case 'ledgerUnassigned':
        return (
          <ResellerLedgerLeaf resellerId={reseller.id} name={reseller.name} part="unassigned" />
        );
      case 'settlements':
        return <ResellerSettlementsPanel resellerId={reseller.id} />;
      case 'partnerAdministrators':
        return (
          <PeopleLeafLayout
            caption={
              <Typography>
                {t(
                  'partners.partnerAdministrators.caption',
                  'Sign in to the reseller console to manage this partner.',
                )}
              </Typography>
            }
            invitations={
              <Box component="section" aria-labelledby="reseller-invitations">
                <ResellerInvitationsPanel
                  resellerId={reseller.id}
                  refreshKey={invitationsRefreshKey}
                />
              </Box>
            }
          >
            <ResellerUsersPanel resellerId={reseller.id} onInvited={onInvited} />
          </PeopleLeafLayout>
        );
      case 'ghl':
        return <ResellerGhlPanel resellerId={reseller.id} />;
      default:
        return (
          <PartnerTenantPanel
            tab={tab}
            reseller={reseller}
            load={tenantLoad}
            tenantDashboard={tenantDashboard}
            onCreate={() => setCreateOpen(true)}
          />
        );
    }
  };

  const localNav = hasLocalNav(activeSection);
  const groups = activeSection === 'overview' ? [] : GROUPS[activeSection];
  const leafLabel = (tab: TabKey) => t(`partners.tab.${tab}`, TAB_LABELS[tab]);

  const sectionTabLabel = (section: SectionKey): ReactNode => {
    const label = sectionLabel(section);
    if (section !== 'settings' || settings.count === 0) return label;
    // The count is drawn for the eye; the tab's name (`sectionTabName`) says it in words.
    return (
      <Box component="span">
        {label}
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: 'inline-block',
            ml: 0.75,
            verticalAlign: 'middle',
            minWidth: 20,
            height: 20,
            px: 0.5,
            borderRadius: 10,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '20px',
            textAlign: 'center',
          }}
        >
          {settings.count}
        </Box>
      </Box>
    );
  };
  /** "Settings, 3 need you": one exact name in every engine (no spacing from the badge's layout). */
  const sectionTabName = (section: SectionKey): { 'aria-label'?: string } =>
    section === 'settings' && settings.count > 0
      ? {
          'aria-label': `${sectionLabel(section)}${t('partners.needsYou.count', ', {{count, number}} need you', { count: settings.count })}`,
        }
      : {};

  const panel = (
    <Box
      id={`partner-panel-${activeTab}`}
      role="tabpanel"
      aria-labelledby={`partner-section-${activeSection}`}
    >
      <PanelErrorBoundary key={activeTab}>{renderPanel(activeTab)}</PanelErrorBoundary>
    </Box>
  );

  const dotLabel = t('partners.needsYou.dot', ' — needs you');

  return (
    <>
      {headerActions.banners}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeSection}
          onChange={(_event, value: SectionKey) => selectTab(firstTab(value))}
          aria-label={t('partners.sectionsAria', 'Partner sections')}
          variant="scrollable"
          scrollButtons="auto"
        >
          {SECTION_KEYS.map((section) => (
            <Tab
              key={section}
              value={section}
              label={sectionTabLabel(section)}
              {...sectionTabName(section)}
              id={`partner-section-${section}`}
              aria-controls={`partner-panel-${section === activeSection ? activeTab : firstTab(section)}`}
            />
          ))}
        </Tabs>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        {t(`partners.caption.${activeSection}`, CAPTIONS[activeSection])}
      </Typography>

      {localNav ? (
        <LocalNav
          ariaLabel={sectionLabel(activeSection)}
          groupIdPrefix="partner-group"
          activeKey={activeTab}
          onNavigate={resetTransient}
          groups={groups.map((group) => ({
            key: group.key,
            label: t(`partners.group.${group.key}`, GROUP_LABELS[group.key]),
            items: group.tabs.map((tab: TabKey) => ({
              key: tab,
              label: leafLabel(tab),
              to: `?tab=${tab}`,
              state: leafState,
              ...(settings.targets.has(tab) ? { dot: true, dotLabel } : {}),
            })),
          }))}
        >
          {panel}
        </LocalNav>
      ) : (
        panel
      )}
      {headerActions.dialogs}
      <CreateTenantDialog
        open={createOpen}
        resellers={[]}
        presetReseller={reseller}
        onClose={() => setCreateOpen(false)}
        onCreated={(_slug, billingWarning) => {
          setCreateOpen(false);
          onReload();
          if (billingWarning) onBillingWarning(billingWarning);
        }}
      />
    </>
  );
}

function NoticesRegion({ resellerId }: { resellerId: number }) {
  const { t } = useCopy();
  return (
    <SectionCard title={t('resellers.notices.sent', 'Notices sent')} id="reseller-notices-sent">
      <ResellerNoticesPanel resellerId={resellerId} />
    </SectionCard>
  );
}
